import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiPlus, FiArrowLeft, FiEdit2, FiTrash2, FiCornerDownRight, FiList, FiGrid, FiCheck } from 'react-icons/fi';
import { api } from '../api';
import { useMe } from '../App';
import { findPerm } from '../perm';
import { money, CampaignModal } from './Campaigns';
import OdooKanban from '../views/OdooKanban';
import '../views/odoo-views.css';
import { ProductLinesEditor, ProductSummary, needsProducts, linesFromTask, linesProblem, linesForSave, emptyLine } from './TaskProducts';
import { ActivityTypeTag, ActivityTypeFilter, ActivityTypeSelect, matchesActivityType, filterActivityGroups, isCampaignPlan, taskBoardRecords, PlanTag } from './ActivityType';

export function DueBadge({ task }) {
  if (task.status === 'done') return <span className="pill" style={{ background: '#E9F3EC', color: '#3B7A4E' }}>Done</span>;
  if (task.days_overdue === null || task.days_overdue === undefined) return null;
  const d = Number(task.days_overdue);
  if (d > 0) return <span className="pill" style={{ background: '#FBEEEA', color: '#B4432E' }}>{d} day{d !== 1 ? 's' : ''} delayed</span>;
  const left = -d;
  if (left === 0) return <span className="pill" style={{ background: '#FDF3E3', color: '#A87B2D' }}>Due today</span>;
  if (left <= 3) return <span className="pill" style={{ background: '#FDF3E3', color: '#A87B2D' }}>Due in {left} day{left !== 1 ? 's' : ''}</span>;
  return <span className="pill" style={{ background: '#E9F3EC', color: '#3B7A4E' }}>Due in {left} days</span>;
}

const STATUS_LABEL = { todo: 'To Do', in_progress: 'In Progress', approval: 'Approval', done: 'Done' };
const TASK_COLUMNS = [
  { value: 'todo', label: 'To Do', color: '#8A93A3' },
  { value: 'in_progress', label: 'In Progress', color: '#3A6A96' },
  { value: 'approval', label: 'Approval', color: '#B99A5B' },
  { value: 'done', label: 'Done', color: '#3B7A4E' },
];

export default function CampaignDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const me = useMe();
  const perm = findPerm(me.modules, 'campaigns');
  const [data, setData] = useState(null);
  const [taskModal, setTaskModal] = useState(null);
  const [campModal, setCampModal] = useState(false);
  const [taskView, setTaskView] = useState('list');
  const [typeFilter, setTypeFilter] = useState('all');
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    try { setData(await api('/campaigns/' + id)); } catch (e) { setToast(e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  function flash(m) { setToast(m); setTimeout(() => setToast(''), 2600); }

  async function setStatus(task, status) {
    try { await api(`/tasks/${task.id}/status`, { method: 'PATCH', body: { status } }); load(); }
    catch (e) { flash(e.message); }
  }
  async function sendToStore(task) {
    if (!window.confirm(`Send "${task.title}" to the store as a requirement?`)) return;
    try {
      await api(`/campaigns/${id}/tasks/${task.id}/send-to-store`, { method: 'POST' });
      flash('Sent to store — requirement created'); load();
    } catch (e) { flash(e.message); }
  }
  async function removeTask(task) {
    const sent = task.sent_to_store || (task.children || []).some((c) => c.sent_to_store);
    if (!window.confirm(`Delete ${isCampaignPlan(task) ? 'plan' : 'task'} "${task.title}"${task.children?.length ? ' and all tasks under it' : ''}?`
      + (sent ? '\n\nThe store requirement already created from it stays in Inventory > Requirements.' : ''))) return;
    try { await api('/tasks/' + task.id, { method: 'DELETE' }); flash(isCampaignPlan(task) ? 'Plan deleted' : 'Task deleted'); load(); }
    catch (e) { flash(e.message); }
  }

  if (!data) return <div className="empty">Loading…</div>;
  const { campaign: c, targets, tasks, total_cost, total_budget } = data;

  // Plans organize work; only their tasks (plus standalone tasks) enter the board.
  const activities = taskBoardRecords(tasks);

  const visibleActivities = activities.filter(t => matchesActivityType(t, typeFilter));
  const visibleGroups = filterActivityGroups(tasks, typeFilter);

  function renderTaskCard(t) {
    return (
      <>
        <div className="okb-card-title">
          {t._parentTitle && <FiCornerDownRight style={{ verticalAlign: '-2px', marginRight: 6, color: 'var(--brass,#B08D57)' }} />}
          {t.title}
        </div>
        <div className="okb-card-row"><ActivityTypeTag value={t.activity_type} /></div>
        {t._parentTitle && <div className="okb-card-row" style={{ marginTop: 0 }}><span style={{ fontStyle: 'italic' }}>under {t._parentTitle}</span></div>}
        <div className="okb-card-row">
          <span>{t.assignee_name || 'Unassigned'}</span>
          {Number(t.cost) > 0 && <><span>·</span><span style={{ fontWeight: 600, color: '#1E2733' }}>{money(t.cost)}</span></>}
        </div>
        <div className="okb-card-row">
          {t.due_date && <span>Due {t.due_date.slice(0, 10)}</span>}
          <DueBadge task={t} />
        </div>
      </>
    );
  }

  return (
    <div>
      <button className="btn btn-sm" style={{ marginBottom: 14 }} onClick={() => nav('/m/campaigns')}>
        <FiArrowLeft /> All campaigns
      </button>

      <div className="camp-summary">
        <div className="camp-main">
          <div className="page-eyebrow">Campaign</div>
          <h1 className="page-title" style={{ margin: '6px 0 8px' }}>{c.name}</h1>
          {c.objective && <p className="page-desc" style={{ margin: 0 }}>{c.objective}</p>}
          {perm.can_edit && (
            <button className="btn btn-sm" style={{ marginTop: 14 }} onClick={() => setCampModal(true)}>
              <FiEdit2 /> Edit campaign
            </button>
          )}
        </div>
        <div className="camp-stats">
          <div className="stat">
            <div className="stat-label">Budget vs Spent</div>
            <div className="stat-value">{money(total_cost)} <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 400 }}>/ {money(total_budget || 0)}</span></div>
            {Number(total_budget) > 0 && (() => {
              const pct = Math.min(100, Math.round((Number(total_cost) / Number(total_budget)) * 100));
              const over = Number(total_cost) > Number(total_budget);
              const rem = Number(total_budget) - Number(total_cost);
              return (<>
                <div className="bar" style={{ marginTop: 6 }}><div className="bar-fill" style={{ width: pct + '%', background: over ? '#B4432E' : (pct >= 90 ? '#A87B2D' : '#3B7A4E') }} /></div>
                <div style={{ fontSize: 12.5, color: over ? '#B4432E' : 'var(--muted)', marginTop: 4 }}>
                  {over ? `Over by ${money(-rem)}` : `${money(rem)} remaining`} ({pct}%)
                </div>
              </>);
            })()}
          </div>
          <div className="stat"><div className="stat-label">Brand</div><div className="stat-value" style={{ fontSize: 18 }}>{c.brand_name || '—'}</div></div>
          <div className="stat">
            <div className="stat-label">Targets</div>
            {(!targets || targets.length === 0) && <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>None set</div>}
            {targets && targets.map((t) => {
              const p = t.target_value && t.achievement_value ? Math.min(100, Math.round((t.achievement_value / t.target_value) * 100)) : null;
              return (
                <div key={t.id} style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{t.metric}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                    {t.achievement_value ? Number(t.achievement_value).toLocaleString('en-IN') : 0} / {t.target_value ? Number(t.target_value).toLocaleString('en-IN') : '—'} {t.unit || ''}
                    {p !== null && <span style={{ color: 'var(--brass)', marginLeft: 5 }}>({p}%)</span>}
                  </div>
                  {p !== null && <div className="bar"><div className="bar-fill" style={{ width: p + '%' }} /></div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mod-head" style={{ marginTop: 26 }}>
        <div className="titles"><h1 style={{ fontSize: 19 }}>Plans & tasks</h1></div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div className="olv-viewswitch">
            <button className={'olv-vs' + (taskView === 'list' ? ' on' : '')} onClick={() => setTaskView('list')} title="Plans and tasks list"><FiList /></button>
            <button className={'olv-vs' + (taskView === 'kanban' ? ' on' : '')} onClick={() => setTaskView('kanban')} title="Task board"><FiGrid /></button>
          </div>
          {perm.can_create && <button className="btn btn-dark" onClick={() => setTaskModal({})}><FiPlus /> Add task</button>}
        </div>
      </div>

      <p className="activity-scope-note">Plans group the work. Add tasks beneath a plan to assign and track the individual steps.</p>
      <ActivityTypeFilter value={typeFilter} onChange={setTypeFilter} count={visibleActivities.length} total={activities.length} />
      {visibleActivities.length === 0 && activities.length > 0 ? <div className="table-card"><div className="empty"><h3>No matching tasks</h3><p>Choose another task type or clear the filter.</p><button className="btn" onClick={() => setTypeFilter('all')}>Show all types</button></div></div> : taskView === 'kanban' ? (
        activities.length === 0 ? (
          <div className="table-card"><div className="empty"><h3>No activities yet</h3><p>Add the first activity for this campaign.</p></div></div>
        ) : (
          <OdooKanban columns={TASK_COLUMNS} groupField="status" records={visibleActivities}
            getKey={(t) => t.id} onCardClick={(t) => perm.can_edit && setTaskModal({ task: t })}
            renderCard={renderTaskCard} onMove={(t, s) => setStatus(t, s)} canMove={perm.can_edit} />
        )
      ) : (
        <div className="table-card">
          {tasks.length === 0 ? (
            <div className="empty"><h3>No activities yet</h3><p>Add the first activity for this campaign.</p></div>
          ) : (
            <table className="data">
              <thead><tr>
                <th style={{ width: '32%' }}>Activity</th><th>Assigned</th><th>Due</th><th>Status</th><th>Timeline</th>
                <th style={{ textAlign: 'right' }}>Cost</th><th style={{ textAlign: 'right' }}>Actions</th>
              </tr></thead>
              <tbody>
                {visibleGroups.map(({ task: t, children, contextOnly }) => (
                  <React.Fragment key={t.id}>
                    <TaskRow task={t} contextOnly={contextOnly} perm={perm} onStatus={setStatus} onSendStore={sendToStore} onEdit={(x) => setTaskModal({ task: x })}
                      onSub={(x) => setTaskModal({ parent: x })} onDelete={removeTask} />
                    {children.map((s) => (
                      <TaskRow key={s.id} task={s} sub perm={perm} onStatus={setStatus} onSendStore={sendToStore}
                        onEdit={(x) => setTaskModal({ task: x })} onDelete={removeTask} />
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {taskModal && <TaskModal campaignId={id} parent={taskModal.parent} task={taskModal.task}
        onClose={() => setTaskModal(null)} onSaved={(m) => { setTaskModal(null); flash(m); load(); }} />}
      {campModal && <CampaignModal campaign={c} initialTargets={targets} onClose={() => setCampModal(false)}
        onSaved={() => { setCampModal(false); flash('Campaign updated'); load(); }} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function TaskRow({ task: t, sub, contextOnly, perm, onStatus, onEdit, onSub, onDelete, onSendStore }) {
  const rolled = !sub && isCampaignPlan(t);
  return (
    <tr className={rolled ? 'campaign-plan-row' : undefined} style={{ opacity: t.status === 'done' ? 0.6 : 1 }}>
      <td style={{ fontWeight: sub ? 400 : 600, paddingLeft: sub ? 40 : undefined }}>
        {sub && <FiCornerDownRight style={{ verticalAlign: '-2px', marginRight: 7, color: 'var(--brass)' }} />}
        {t.title}
        <div style={{ marginTop: 5 }}><>{rolled ? <PlanTag /> : <ActivityTypeTag value={t.activity_type} />}</>{contextOnly && <span className="activity-context-label">{rolled ? `${t.children.length} task${t.children.length === 1 ? '' : 's'} in this plan` : 'Parent context'}</span>}</div>
        {!rolled && <ProductSummary task={t} />}
      </td>
      <td style={{ color: 'var(--muted)' }}>{t.assignee_name || '—'}</td>
      <td style={{ color: 'var(--muted)' }}>{t.due_date ? t.due_date.slice(0, 10) : '—'}</td>
      <td>
        {perm.can_edit ? (
          <select className="status-select" value={t.status} onChange={(e) => onStatus(t, e.target.value)}>
            <option value="todo">To Do</option>
            <option value="in_progress">In Progress</option>
            <option value="approval">Approval</option>
            <option value="done">Done</option>
          </select>
        ) : STATUS_LABEL[t.status]}
      </td>
      <td><DueBadge task={t} /></td>
      <td style={{ textAlign: 'right', fontWeight: 600 }}>
        {money(t.cost)}{rolled && <span title="Total cost of all tasks in this plan" style={{ color: 'var(--brass)', marginLeft: 4 }}>Σ</span>}
      </td>
      <td>
        <div className="row-actions">
          {!sub && onSub && perm.can_create && (
            <button className="btn btn-sm" title={rolled ? "Add task to this plan" : "Add a task beneath this item; it becomes a plan"} onClick={() => onSub(t)}><FiPlus /> Add task</button>
          )}
          {t.activity_type === 'procurement' && !rolled && perm.can_edit && onSendStore && (t.sent_to_store || t.products?.length > 0) && (
            t.sent_to_store
              ? <span className="pill" style={{ background: '#E9F3EC', color: '#3B7A4E', display: 'inline-flex', alignItems: 'center', gap: 4 }}><FiCheck /> Sent</span>
              : <button className="btn btn-sm" style={{ color: '#017E84' }} title="Create a store requirement from this activity" onClick={() => onSendStore(t)}>Send to store</button>
          )}
          {perm.can_edit && <button className="btn btn-sm" onClick={() => onEdit(t)}><FiEdit2 /></button>}
          {perm.can_delete && <button className="btn btn-sm btn-danger" onClick={() => onDelete(t)}><FiTrash2 /></button>}
        </div>
      </td>
    </tr>
  );
}

function TaskModal({ campaignId, parent, task, onClose, onSaved }) {
  const edit = !!task;
  const hasChildren = edit && isCampaignPlan(task);
  const [opts, setOpts] = useState({ managers: [] });
  const [f, setF] = useState({
    title: task?.title || '', description: task?.description || '',
    assigned_to: task?.assigned_to || '', due_date: task?.due_date?.slice(0, 10) || '',
    cost: task?.cost ?? '', budget: task?.budget ?? '', status: task?.status || 'todo',
    activity_type: task?.activity_type || 'task',
  });
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  const [lines, setLines] = useState(() => { const l = linesFromTask(task); return l.length ? l : [emptyLine()]; });
  const [products, setProducts] = useState([]); const [prodErr, setProdErr] = useState('');
  const [touched, setTouched] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const withProducts = !hasChildren && needsProducts(f.activity_type);
  const productProblem = withProducts ? linesProblem(lines) : '';
  useEffect(() => { api('/users/form-options').then(setOpts).catch(() => {}); }, []);
  useEffect(() => {
    api('/tasks/product-options').then(setProducts).catch((e) => setProdErr('Could not load products: ' + e.message));
  }, []);
  async function save() {
    setError(''); setTouched(true);
    if (productProblem) return; // shown under the product lines
    setBusy(true);
    const body = { ...f, cost: f.cost === '' ? 0 : Number(f.cost), budget: f.budget === '' ? 0 : Number(f.budget) };
    if (!hasChildren) body.products = withProducts ? linesForSave(lines) : [];
    try {
      if (edit) { await api('/tasks/' + task.id, { method: 'PUT', body }); onSaved(hasChildren ? 'Plan updated' : 'Task updated'); }
      else {
        if (parent) body.parent_task_id = parent.id;
        await api(`/campaigns/${campaignId}/tasks`, { method: 'POST', body });
        onSaved(parent ? 'Task added to plan' : 'Task added');
      }
    } catch (e) { setError(e.message); setBusy(false); }
  }
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: withProducts ? 600 : 520 }} onClick={(e) => e.stopPropagation()}>
        <h2>{edit ? (hasChildren ? 'Edit plan' : 'Edit task') : parent ? `Add task to “${parent.title}”` : 'Add task'}</h2>
        <div className="field"><label>Title</label>
          <input value={f.title} autoFocus onChange={set('title')} placeholder="What needs to be done?" /></div>
        {!hasChildren && <ActivityTypeSelect value={f.activity_type} onChange={value => setF(current => ({ ...current, activity_type: value }))} />}
        {withProducts && <ProductLinesEditor type={f.activity_type} lines={lines} onChange={(l) => { setLines(l); setError(''); }}
          products={products} loadError={prodErr} showProblem={touched} />}
        <div className="field"><label>Description</label>
          <input value={f.description} onChange={set('description')} placeholder="Optional details" /></div>
        <div className="grid2">
          <div className="field"><label>{hasChildren ? 'Plan owner' : 'Assigned to'}</label>
            <select value={f.assigned_to} onChange={set('assigned_to')}>
              <option value="">Unassigned</option>
              {opts.managers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select></div>
          <div className="field"><label>End date</label>
            <input type="date" value={f.due_date} onChange={set('due_date')} /></div>
        </div>
        <div className="grid2">
          <div className="field"><label>Budget (planned ₹){hasChildren && ' — total of tasks in this plan'}</label>
            <input type="number" value={f.budget} onChange={set('budget')} disabled={hasChildren}
              placeholder={hasChildren ? 'Auto-calculated' : 'e.g. 15000'} /></div>
          <div className="field"><label>Cost (actual ₹){hasChildren && ' — total of tasks in this plan'}</label>
            <input type="number" value={f.cost} onChange={set('cost')} disabled={hasChildren}
              placeholder={hasChildren ? 'Auto-calculated' : 'e.g. 12000'} /></div>
        </div>
        {error && <div className="inline-error">{error}</div>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-dark" onClick={save} disabled={busy || !f.title.trim()} title={productProblem || undefined}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
